import { Button, Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  PREFERENCE_TYPE,
  PREFERENCES_TYPE_MAPPER,
} from "../../../../../../constants";
import { ADDRESS_INITIAL_VALUES } from "../../../../../../constants/provider/patient/add-new-patient/add-new-patient-form";
import { useCreatePharmacyLabRadiology } from "../../../../../../queries/patient-chart/order/use-create-pharmacy-lab-radiology";
import {
  getErrorSnackbar,
  getSuccessSnackbar,
} from "../../../../../../utils/utils";
import CustomSelect from "../../../../../CustomSelect";
import Flex from "../../../../../Flex";
import Input from "../../../../../Input";
import MaskInput from "../../../../../MaskInput";
import SideSheetDrawer from "../../../../../SidesheetDrawer";
import AddressCommon from "./AddressCommon";
import { useSnackbar } from "../../../../../../hooks/useSnackbar";
import NumberInput from "../../../../../NumberInput";
import { getStoredRealm } from "@/utils/auth-storage";

interface PatientLabPharmacyRadiologyFormDrawerProps {
  opened: boolean;
  handleCloseModal: () => void;
}

const initialValues = {
  preferenceType: "",
  name: "",
  address: ADDRESS_INITIAL_VALUES,
  contactNumber: "",
  faxNumber: "",
};

const PatientLabPharmacyRadiologyFormDrawer = ({
  opened,
  handleCloseModal,
}: PatientLabPharmacyRadiologyFormDrawerProps) => {
  const { mutateAsync: createPharmacyLabRadiology } =
    useCreatePharmacyLabRadiology();

  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  const validationSchema = Yup.object({
    preferenceType: Yup.string().required().label("Preference Type"),
    name: Yup.string().when("preferenceType", (preferenceType, schema) => {
      const dynamicLabel =
        preferenceType.toLocaleString() === "LAB"
          ? "Lab Name"
          : preferenceType.toLocaleString() === "PHARMACY"
            ? "Pharmacy Name"
            : preferenceType.toLocaleString() === "RADIOLOGY"
              ? "Radiology Name"
              : "Name";

      return schema.trim().required().label(dynamicLabel);
    }),
    contactNumber: Yup.string().min(
      12,
      "Contact Number must be at least 8 digits"
    ),
  });

  const preferencesFormik = useFormik({
    initialValues,
    validationSchema: validationSchema,
    onSubmit: handleSave,
  });

  function handleSave(vals: any) {
    const payload = {
      parmacyLabRadiology: vals,
      xTENANTID: getStoredRealm() ?? undefined,
    };
    showToastAndResetForm(createPharmacyLabRadiology(payload as any));
  }

  async function showToastAndResetForm(apiCall: Promise<any>) {
    apiCall
      .then((res) => {
        showSnackbar(getSuccessSnackbar(res));

        queryClient.invalidateQueries({
          queryKey: ["patient-orders-list"],
        });

        closeAndResetModal();
      })
      .catch((err) => {
        showSnackbar(getErrorSnackbar(err));
      });
  }

  function closeAndResetModal() {
    handleCloseModal();
    preferencesFormik.resetForm();
  }

  return (
    <SideSheetDrawer
      open={opened}
      title="Add New Preferences"
      w="800px"
      footerActions={
        <Button
          variant="contained"
          type="submit"
          onClick={preferencesFormik.submitForm}
        >
          Save Preferences
        </Button>
      }
      onClose={closeAndResetModal}
    >
      <Flex flexDirection={"column"} gap={2} mt={2}>
        <Grid size={6}>
          <CustomSelect
            required
            label="Preferences Type"
            value={preferencesFormik.values.preferenceType}
            options={PREFERENCE_TYPE}
            onChange={(val) =>
              preferencesFormik.setFieldValue("preferenceType", val)
            }
            placeholder="Select"
            error={
              preferencesFormik.touched?.preferenceType &&
              preferencesFormik?.errors?.preferenceType
            }
          />
        </Grid>
        <Input
          label={`${
            preferencesFormik.values.preferenceType
              ? PREFERENCES_TYPE_MAPPER[preferencesFormik.values.preferenceType]
              : "Preference Name"
          }`}
          required
          error={
            preferencesFormik.touched?.name && preferencesFormik?.errors?.name
          }
          textFieldProps={{
            placeholder: `${
              preferencesFormik.values.preferenceType
                ? `Enter ${
                    PREFERENCES_TYPE_MAPPER[
                      preferencesFormik.values.preferenceType
                    ]
                  }`
                : "Enter Preference Name"
            }`,
            onChange: preferencesFormik.handleChange,
            onBlur: preferencesFormik.handleBlur,
            name: "name",
            value: preferencesFormik.values.name,
          }}
        />
        <AddressCommon formik={preferencesFormik} formikKey={`address`} />
        <Grid container spacing={2}>
          <Grid size={6}>
            <NumberInput
              label="Fax Number"
              textFieldProps={{
                placeholder: "Enter Fax Number",
                onChange: preferencesFormik.handleChange,
                onBlur: preferencesFormik.handleBlur,
                name: "faxNumber",
                value: preferencesFormik.values.faxNumber,
              }}
            />
          </Grid>

          <Grid size={6}>
            <MaskInput
              mask={"(999) 999-9999"}
              label="Contact Number"
              name="contact"
              error={preferencesFormik.errors?.contactNumber}
              placeholder="Enter Contact Number"
              value={preferencesFormik.values.contactNumber}
              onChange={(e) =>
                preferencesFormik.setFieldValue("contactNumber", e.target.value)
              }
            />
          </Grid>
        </Grid>
      </Flex>
    </SideSheetDrawer>
  );
};

export default PatientLabPharmacyRadiologyFormDrawer;
